import type { StatusDetail } from "../common/status";
import { isOfType } from "../utils";

export const EVALUATOR_METRIC_TYPES = [
  "CoverageProbability",
  "F1",
  "AreaUnderROC",
  "PVA",
  "RSquared",
  "RMSE",
  "StandardRMSE",
  "NDME",
] as const;

export type EvaluatorMetricType = (typeof EVALUATOR_METRIC_TYPES)[number];

interface BaseEvaluatorMetric {
  type: EvaluatorMetricType;
}

interface BasicEvalutorMetric extends BaseEvaluatorMetric {
  type: Exclude<EvaluatorMetricType, "CoverageProbability">;
}

export interface CoverageProbabilityMetric extends BaseEvaluatorMetric {
  type: "CoverageProbability";
  coverage_level?: string | number;
}
export type EvaluatorMetric = BasicEvalutorMetric | CoverageProbabilityMetric;
export const isCoverageProbabilityMetric = isOfType<
  CoverageProbabilityMetric,
  EvaluatorMetric
>("CoverageProbability");

export const DEFAULT_COVERAGE_LEVEL = 0.683;
export const DEFAULT_N_FOLDS = 5;
export const DEFAULT_N_TRIALS = 3;

export type CrossValidationEvaluator = {
  type: "CrossValidationEvaluator";
  name: string;
  description: string;
  responses: string[];
  n_folds: number;
  n_trials: number;
  metrics: EvaluatorMetric[];
} & (
  | {
      ignore_when_grouping: string[];
      group_by_keys?: never;
    }
  | {
      ignore_when_grouping?: never;
      group_by_keys: string[]; // Deprecated in favor of `ignore_when_grouping`
    }
);

// we currently only have 1 evaluator type, but that will change
export type PredictorEvaluator = CrossValidationEvaluator;

export const isCrossValidationEvaluator = (
  pe: PredictorEvaluator,
): pe is CrossValidationEvaluator => pe.type === "CrossValidationEvaluator";

export interface PredictorEvaluationWorkflow {
  id: string;
  name: string;
  description: string;
  evaluators: PredictorEvaluator[];
  status: string;
  status_description: string;
  status_detail: StatusDetail[];
  archived: boolean;
  created_by: string;
  create_time: number;
  updated_by: string;
  update_time: number;
}

export const isPredictorEvaluationWorkflowSettled = (
  workflow?: PredictorEvaluationWorkflow | null,
) => {
  return workflow && workflow.status !== "INPROGRESS";
};

export const isPredictorWorkflowReady = (
  workflow?: PredictorEvaluationWorkflow | null,
) => {
  return workflow?.status === "SUCCEEDED";
};
